import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SimpleCaptcha from "./SimpleCaptcha";
import { generateHash } from "../API/hashUtil";
import { MyContext } from "../MyContextApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import CustomAlertDialog from "./Dialog";
import { useAuth } from "../context/AuthContext";
import api from "../API/axiosInstance";

import CsiNeural from "./CsiNeural";
import cdacLogo from "../images/cdac-csmart.png";

/* Direction C — two-panel split.
   Left: navy panel, neural-mesh graphic behind the same copy
   Direction A uses. Right: white panel with the form.
   Logic is byte-identical to the current SignIn. */
import "./../styles/SignInSplit.css";

const SignInSplit = () => {
  const { captchaData, setCaptchaData, showDialog, setShowDialog } =
    useContext(MyContext);
  const { user, login } = useAuth();

  const navigate = useNavigate();
  const errRef = useRef();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userCaptchaInput, setUserCaptchaInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const validateCaptcha = async () => {
    try {
      // Use axios (api) instead of raw fetch — sends cookies automatically
      const response = await api.post("/captcha2/validate", {
        captchaId: captchaData.captchaId,
        captcha: userCaptchaInput,
      });

      return response.status === 200;
    } catch {
      toast.error("Invalid captcha. Please try again.");
      setRefreshTrigger((prev) => !prev);
      return false;
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!captchaData?.captchaImage || !captchaData?.captchaId) {
      toast("Please complete the captcha.");
      return;
    }

    const isCaptchaValid = await validateCaptcha();
    if (!isCaptchaValid) return;

    try {
      setIsSubmitting(true);

      const HPPcode = generateHash({ userName, password });

      await login({
        username: userName,
        password,
        HPPcode,
      });

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setRefreshTrigger((prev) => !prev);

      if (!err?.response) {
        toast.error("No Server Response");
      } else if (err.response.status === 401) {
        toast.error("Invalid Credentials!");
      } else if (err.response.status === 423) {
        toast.error(
          "Your account is locked due to too many failed login attempts. Try again after 24 hours.",
        );
      } else if (err.response.status === 403) {
        toast.error("User is not approved by admin");
      } else if (err.response.data?.error) {
        toast.error(err.response.data.error);
        setLoginAttempts(err.response.data.login_attempts || 0);
      } else {
        toast.error("Login Failed");
      }

      errRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirm = () => {
    setShowDialog(false);
  };

  return (
    <div className="csp">
      <CustomAlertDialog
        open={showDialog}
        onConfirm={handleConfirm}
        title="Session Expired"
        message="Your session has expired. Please sign in again."
        iconType="warning"
        buttonText="OK"
      />

      {/* ══════════ LEFT — navy panel ══════════ */}
      <AuthBrandPanel />

      {/* ══════════ RIGHT — form panel ══════════ */}
      <section className="csp-panel">
        <Link to="/" className="csp-back">
          <ArrowLeft />
          Back
        </Link>

        <div className="csp-form-wrap">
          <div className="csp-head">
            <h2 className="csp-title">Sign In</h2>
            <p className="csp-sub">Enter your credentials to continue</p>
          </div>

          <form className="csp-form" onSubmit={handleSignIn} autoComplete="off">
            <div className="csp-field">
              <label htmlFor="user_id" className="csp-label">
                Username or Email ID
              </label>
              <div className="csp-input-wrap">
                <input
                  className="csp-input"
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  id="user_id"
                  name="user_id"
                  placeholder="Enter your username or email id"
                  required
                  autoComplete="off"
                  readOnly
                  onFocus={(e) => e.target.removeAttribute("readonly")}
                />
              </div>
            </div>

            <div className="csp-field">
              <div className="csp-label-row">
                <label htmlFor="password" className="csp-label">
                  Password
                </label>
                <Link to="/password/reset" className="csp-forgot">
                  Forgot password?
                </Link>
              </div>
              <div className="csp-input-wrap">
                <input
                  className="csp-input csp-input--password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  required
                  autoComplete="new-password"
                  readOnly
                  onFocus={(e) => e.target.removeAttribute("readonly")}
                />

                <button
                  type="button"
                  className="csp-eye"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="csp-field">
              <label className="csp-label" htmlFor="captchaInput">
                CAPTCHA Verification
              </label>

              <div className="csp-captcha">
                <SimpleCaptcha
                  onCaptchaChange={setCaptchaData}
                  refreshTrigger={refreshTrigger}
                />
              </div>

              <input
                className="csp-input"
                type="text"
                value={userCaptchaInput}
                onChange={(e) => setUserCaptchaInput(e.target.value)}
                id="captchaInput"
                name="captchaInput"
                placeholder="Enter CAPTCHA text"
                required
                autoComplete="off"
              />
            </div>

            {loginAttempts > 0 && (
              <div className="csp-attempts">
                <div className="csp-attempts__row">
                  <span className="csp-attempts__label">
                    Failed login attempts
                  </span>
                  <span className="csp-attempts__num">{loginAttempts}</span>
                </div>

                {loginAttempts >= 5 && (
                  <div className="csp-attempts__lock">
                    This account has been locked for 24 hours due to multiple
                    failed attempts.
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              className={`csp-submit${isSubmitting ? " is-submitting" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing In…" : "Sign In"}
              {!isSubmitting && <ArrowRight />}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

/* ─────────────────────── shared shell ───────────────────────
   Left navy brand panel — identical across every csp-* auth page
   (sign in, reset password, ...) so the "split" layout stays in
   sync. Exported so other auth pages can render the same shell. */
export function AuthBrandPanel() {
  return (
    <section className="csp-brand">
      {/* techy backdrop: neural mesh with travelling signals */}
      <CsiNeural opacity={0.55} density={1} speed={1} />
      <div className="csp-brand__orb csp-brand__orb--a" aria-hidden="true" />
      <div className="csp-brand__orb csp-brand__orb--b" aria-hidden="true" />

      {/* logo, top-left */}
      <Link to="/" className="csp-lockup" aria-label="C-SMART home">
        <img src={cdacLogo} alt="CDAC · C-SMART" className="csp-lockup__img" />
      </Link>

      <div className="csp-brand__body">
        <h1 className="csp-h1">
          Sign in to
          <br />
          <span className="csp-h1__grad">C-SMART</span>
        </h1>

        <p className="csp-lead">
          Authorised access only. Use your institutional credentials to enter
          the platform. Contact your administrator if you need access.
        </p>

        <div className="csp-note">
          <ShieldIcon />
          <span>
            This is a secure Government of India system. Activity may be
            monitored.
          </span>
        </div>
      </div>

      <p className="csp-copy">
        © {new Date().getFullYear()} Centre for Development of Advanced
        Computing
      </p>
    </section>
  );
}

/* ─────────────────────── icons ─────────────────────── */

export function ArrowRight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

export function ArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export default SignInSplit;
